import { useMutation, useQuery } from '@tanstack/react-query'
import type { DailyRaw } from '../src/domain'

export type MetaImportInfo = {
  last_successful_import_at: string | null
  import_status: string
  row_count_imported: number
  data_date_from: string | null
  data_date_to: string | null
  error_message: string | null
}

type DashboardApiRow = {
  date: string
  source: 'apple_ads' | 'meta_ads' | 'superwall'
  spend: number | null
  impressions: number | null
  clicks: number | null
  currency: string
  gross_revenue: number | null
  proceeds: number | null
  refunds: number | null
  net_proceeds: number | null
  trials_started: number | null
  trial_conversions: number | null
  initial_purchases: number | null
  renewals: number | null
}

type MetaDailyResponse = {
  rows: DashboardApiRow[]
  imports: Array<MetaImportInfo & { source: string }>
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'https://intent-dashboard-ingestion.saad-saziz.workers.dev'

export class DashboardApiError extends Error { constructor(message:string,public status:number){super(message)} }

async function fetchMetaDaily(days: number,token:string): Promise<MetaDailyResponse> {
  const response = await fetch(`${API_BASE_URL}/api/dashboard/daily?days=${days}`, { cache: 'no-store',headers:{Authorization:`Bearer ${token}`} })
  if (!response.ok) throw new DashboardApiError(`Data API returned ${response.status}`,response.status)
  return response.json() as Promise<MetaDailyResponse>
}

function toDailyRows(rows: DashboardApiRow[]): DailyRaw[] {
  const byDate = new Map<string, DailyRaw>()
  for (const row of rows) {
    const daily = byDate.get(row.date) ?? {
    date: row.date,
    appleAdsSpend: null,
    metaAdsSpend: null,
    appleImpressions: null,
    metaImpressions: null,
    appleClicks: null,
    metaClicks: null,
    installs: null,
    superwallGross: null,
    superwallProceeds: null,
    appleEstimatedProceeds: null,
    appleFinalProceeds: null,
    refunds: null,
    trialsStarted: null,
    trialConversions: null,
    initialPurchases: null,
    renewals: null,
    currency: row.currency ?? 'USD',
    sources: { appleAds: false, metaAds: false, superwall: false, appleSales: false },
    }
    if (row.source === 'apple_ads') {
      daily.appleAdsSpend = Number(row.spend ?? 0); daily.appleImpressions = Number(row.impressions ?? 0); daily.appleClicks = Number(row.clicks ?? 0); daily.sources.appleAds = true
    } else if (row.source === 'meta_ads') {
      daily.metaAdsSpend = Number(row.spend ?? 0); daily.metaImpressions = Number(row.impressions ?? 0); daily.metaClicks = Number(row.clicks ?? 0); daily.sources.metaAds = true
    } else {
      daily.superwallGross = Number(row.gross_revenue ?? 0); daily.superwallProceeds = Number(row.net_proceeds ?? 0); daily.refunds = Number(row.refunds ?? 0)
      daily.trialsStarted = Number(row.trials_started ?? 0); daily.trialConversions = Number(row.trial_conversions ?? 0); daily.initialPurchases = Number(row.initial_purchases ?? 0); daily.renewals = Number(row.renewals ?? 0); daily.sources.superwall = true
    }
    byDate.set(row.date, daily)
  }
  return [...byDate.values()].sort((a,b)=>a.date.localeCompare(b.date))
}

export function useMetaDaily(days = 31,token:string|null,sessionVersion=0) {
  const query = useQuery({
    queryKey: ['dashboard-daily', days, sessionVersion],
    queryFn: () => fetchMetaDaily(days,token!),
    enabled:Boolean(token),
    select: data => ({ rows: toDailyRows(data.rows), importInfo: data.imports.find(item=>item.source==='meta_ads') ?? null, superwallImportInfo: data.imports.find(item=>item.source==='superwall') ?? null, appleAdsImportInfo: data.imports.find(item=>item.source==='apple_ads') ?? null }),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    retry:(count,error)=>error instanceof DashboardApiError&&error.status===401?false:count<2,
  })
  const refreshMutation = useMutation({
    mutationFn: async () => {
      if(!token) throw new DashboardApiError('Unauthorized',401)
      const response = await fetch(`${API_BASE_URL}/run`, { method: 'POST', cache: 'no-store',headers:{Authorization:`Bearer ${token}`} })
      if (!response.ok) throw new Error(`Refresh failed with ${response.status}`)
    },
    onSuccess: async () => { await query.refetch() },
  })
  return { ...query, refresh: refreshMutation.mutate, isRefreshing: refreshMutation.isPending, refreshError: refreshMutation.error }
}
